'use client'

import Link from "next/link";
import {navItems} from "./data";
import {Authenticated, Unauthenticated} from "convex/react";
import {SignInButton} from "@/components/auth/singInButton";
import { User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {useAuthActions} from "@convex-dev/auth/react";
import {useState} from "react";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { usePostHog } from "posthog-js/react";
import { clearActivitySelectorCache } from "@/components/activitySelection/activitySelectorCache";

type Props = {
  user: string | undefined | null
}

export default function NavbarMobile({} : Props) {
  const { signOut } = useAuthActions();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { openSettings } = useCookieConsent();
  const posthog = usePostHog();

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      clearActivitySelectorCache();

      // Track successful sign out
      posthog?.capture('user_signed_out_success', {
        source: 'mobile_navbar',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error("Failed to sign out:", error);
      setIsSigningOut(false);

      // Track failed sign out
      posthog?.capture('user_sign_out_failed', {
        source: 'mobile_navbar',
        error_type: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleMenuItemClick = (item: string) => {
    posthog?.capture('navbar_menu_item_clicked', {
      source: 'mobile_navbar',
      menu_item: item,
      timestamp: new Date().toISOString()
    });
  };

  const handleCookieSettingsClick = () => {
    posthog?.capture('cookie_settings_opened', {
      source: 'mobile_navbar',
      timestamp: new Date().toISOString()
    });
    openSettings();
  };

  return (
    <div className="flex flex-row justify-around items-center">
      <Authenticated>
        {navItems.map((item) => (
          <Link
            key={item.name}
            href={item.path}
            className="flex flex-col items-center p-2 text-center"
            data-attr={`nav-${item.name.toLowerCase()}`}
            onClick={() => handleMenuItemClick(item.name)}>
            <item.icon className="h-5 w-5 mb-1" />
            <span className="text-xs">{item.name}</span>
          </Link>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex flex-col items-center p-2 text-center"
            data-attr="profile-menu-trigger">
            <User className="h-5 w-5 mb-1" />
            <span className="text-xs">Profile</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-48">
            <DropdownMenuItem asChild>
              <Link
                href="/profile"
                data-attr="profile-menu-profile"
                onClick={() => handleMenuItemClick('Profile')}>
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                href="/account"
                data-attr="profile-menu-account"
                onClick={() => handleMenuItemClick('Account')}>
                Account
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                href="/billing"
                data-attr="profile-menu-billing"
                onClick={() => handleMenuItemClick('Billing')}>
                Billing
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                href="/colororder"
                data-attr="profile-menu-colors"
                onClick={() => handleMenuItemClick('Colors')}>
                Colors
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                href="/privacy-policy"
                data-attr="profile-menu-privacy-policy"
                onClick={() => handleMenuItemClick('Privacy Policy')}>
                Privacy Policy
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                href="/terms-of-use"
                data-attr="profile-menu-terms-of-use"
                onClick={() => handleMenuItemClick('Terms of Use')}>
                Terms of Use
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleCookieSettingsClick}
              data-attr="profile-menu-manage-cookies">
              Manage Cookies
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                onClick={handleSignOut}
                href={"/"}
                data-attr="auth-sign-out">
                {isSigningOut ? "Signing out..." : "Sign out"}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Authenticated>
      <Unauthenticated>
        <div data-attr="auth-sign-in-mobile">
          <SignInButton/>
        </div>
      </Unauthenticated>
    </div>
  );
}
